import type { CoverConfig, CoverFieldKey } from '@/types/cover';

/**
 * Pure cover-field resolver. This module is mirrored into the API for the
 * DOCX export (like docx chart-config), so it must stay dependency-free:
 * no React, no app imports beyond the cover types.
 */

/** CFP 06/2019 §2º I prescribes the literal title — never free-text. */
export const COVER_DOCUMENT_TITLE = 'Laudo Psicológico';

/**
 * Resolved text is clamped so a long name degrades to an ellipsis instead of
 * overflowing a template slot — DOCX has no CSS ellipsis, so the clamp must
 * live here, shared by both renderers.
 */
export const COVER_FIELD_MAX_LENGTH = 120;

export const COVER_FIELD_DEFAULT_TEXT: Record<CoverFieldKey, string> = {
    document_title: COVER_DOCUMENT_TITLE,
    subtitle: 'Avaliação Psicológica',
    patient_name: '{{patient_name}}',
    solicitante: 'Solicitante: {{solicitante}}',
    finalidade: '{{finalidade}}',
    psychologist_name: '{{psychologist_name}}',
    crp: 'CRP {{crp}}',
    city_date: '{{city}}, {{date}}',
};

/**
 * Os dados do paciente (`patient_name`, `solicitante`) e a `finalidade`
 * (CFP §2º IV) ficam fora do default: a capa circula fora do consultório, então
 * só nasce com a identificação do psicólogo e o local/data. Todos os três têm
 * fonte de dados e são ligáveis campo a campo no editor de capa.
 */
export const COVER_DEFAULT_VISIBLE_FIELDS: CoverFieldKey[] = [
    'document_title',
    'subtitle',
    'psychologist_name',
    'crp',
    'city_date',
];

export type CoverFieldContext = {
    patient?: {
        full_name?: string;
        referring_professional?: string;
    };
    psychologist?: {
        full_name?: string;
        crp?: string;
        /** Cidade de atuação/emissão do laudo (local, data) — do perfil. */
        city?: string;
        /** UF de atuação (campo próprio, NÃO o crp_state); renderiza "Cidade/UF". */
        state?: string;
    };
    /** Finalidade do laudo (CFP §2º IV) — vem do report, não do paciente. */
    finalidade?: string;
    /** ISO string or Date; defaults to today. */
    date?: string | Date;
};

export type ResolvedCoverFields = Partial<Record<CoverFieldKey, string>>;

const formatCoverDate = (date: string | Date | undefined): string => {
    const value = date ? new Date(date) : new Date();
    if (Number.isNaN(value.getTime())) return '';

    return new Intl.DateTimeFormat('pt-BR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(value);
};

/** "São Paulo/SP" quando há UF, "São Paulo" quando não, "" sem cidade. */
const formatCityWithUf = (city: string | undefined, state: string | undefined): string => {
    const cityName = city?.trim() ?? '';
    const uf = state?.trim() ?? '';
    if (!cityName) return '';

    return uf ? `${cityName}/${uf}` : cityName;
};

/**
 * "São Paulo/SP, 7 de setembro de 2026" — a mesma linha que a capa imprime em
 * `city_date`, reusada pelo bloco de assinatura no fim do laudo. Sem cidade no
 * perfil, degrada para só a data, sem deixar vírgula solta.
 */
export const formatLocalEData = (context: {
    city?: string;
    state?: string;
    date?: string | Date;
}): string =>
    [formatCityWithUf(context.city, context.state), formatCoverDate(context.date)]
        .filter(Boolean)
        .join(', ');

const clampFieldText = (text: string): string => {
    if (text.length <= COVER_FIELD_MAX_LENGTH) return text;

    return `${text.slice(0, COVER_FIELD_MAX_LENGTH - 1).trimEnd()}…`;
};

const TOKEN_PATTERN = /\{\{\s*([a-z_]+)\s*\}\}/g;

/**
 * Resolves a cover config against a patient/psychologist context into the
 * final per-field display strings.
 *
 * - Fields not covered by `config.fields` fall back to the default visible
 *   set and default texts.
 * - `document_title` always resolves to the prescribed literal.
 * - A field whose tokens all resolve empty is dropped (returns no entry), so
 *   "Solicitante:" never renders as a dangling label.
 */
export const resolveCoverFields = (
    config: CoverConfig,
    context: CoverFieldContext,
): ResolvedCoverFields => {
    // "Local, data" = cidade de emissão (psicólogo), com UF quando houver:
    // "São Paulo/SP". Sem cidade, o campo degrada para só a data.
    const cityWithUf = formatCityWithUf(context.psychologist?.city, context.psychologist?.state);

    const tokenValues: Record<string, string> = {
        patient_name: context.patient?.full_name?.trim() ?? '',
        solicitante: context.patient?.referring_professional?.trim() ?? '',
        finalidade: context.finalidade?.trim() ?? '',
        psychologist_name: context.psychologist?.full_name?.trim() ?? '',
        crp: context.psychologist?.crp?.trim() ?? '',
        city: cityWithUf,
        date: formatCoverDate(context.date),
    };

    const resolved: ResolvedCoverFields = {};

    for (const key of Object.keys(COVER_FIELD_DEFAULT_TEXT) as CoverFieldKey[]) {
        if (key === 'document_title') {
            resolved.document_title = COVER_DOCUMENT_TITLE;
            continue;
        }

        const fieldConfig = config.fields?.[key];
        const visible = fieldConfig
            ? fieldConfig.visible
            : COVER_DEFAULT_VISIBLE_FIELDS.includes(key);

        if (!visible) continue;

        const template = fieldConfig?.text ?? COVER_FIELD_DEFAULT_TEXT[key];

        let hasToken = false;
        let hasTokenValue = false;
        const text = template
            .replace(TOKEN_PATTERN, (_, token: string) => {
                hasToken = true;
                const value = tokenValues[token] ?? '';
                if (value) hasTokenValue = true;
                return value;
            })
            .replace(/\s{2,}/g, ' ')
            .replace(/^[\s,·-]+|[\s,·-]+$/g, '');

        if (hasToken && !hasTokenValue) continue;
        if (!text) continue;

        resolved[key] = clampFieldText(text);
    }

    return resolved;
};
